import {
  GraphQLServerUrl,
  RxDatabase,
  RxReplicationWriteToMasterRow,
  WithDeleted,
} from 'rxdb'
import { replicateGraphQL } from 'rxdb/plugins/replication-graphql'

import { graphql } from '@/gql'
import {
  createQueryWithHash,
  pullResponseModifier,
} from '@/system/rxdb/replications/utils.ts'
import { AssessmentAdditionalInfo } from '@/system/rxdb/schemas/assessmentAdditionalInfo'

type PullData =
  | {
      nodes: Array<WithDeleted<AssessmentAdditionalInfo>>
    }
  | { assessmentAdditionalInfo: WithDeleted<AssessmentAdditionalInfo> }

// amount of items we want to pull max for each iteration
const BATCH_SIZE = 20

export const ASSESSMENT_ADDITIONAL_INFO_FRAGMENT = /* GraphQL*/ `
    fragment assessmentAdditionalInfo on AssessmentAdditionalInfo {
        rowId
        updatedAt
        deleted
        assessmentId
        careChangesSuggestionsPersonInNeedOfCare
        careChangesSuggestionsRelativeCaregiver
        careImprovementsSuggestionsPersonInNeedOfCare
        careImprovementsSuggestionsRelativeCaregiver
        differentThanUsualPersonInNeedOfCare
        differentThanUsualRelativeCaregiver
        otherCommentsPersonInNeedOfCare
        otherCommentsRelativeCaregiver
        agreedObjectives
        agreementsOnNextSteps
        openQuestions
        supportSituationCaregiverPerspective
        supportSituationCounselorPerspective
        supportIsEnsured
        supportIsEnsuredReason
        supportSituationImproveMeasures
        supportSituationImproveMeasuresSuggestions
        supportSituationImproveMeasuresSuggestionsOtherComments
        counselingViaVideoConference
        careCounselingIndicatedSection7A
        careCounselingRequestedSection7A
        consentToShareCareSupportInformation
        consentToShareImprovementRecommendations
        consentToSiteInspectionAndToShareInformation
        consentToProcessingCounselingResultsSection7A
        consentToProcessingByInsuranceSection45
        insuranceName
        locationAndDateSection373
        protocolDispatch
        protocolDispatchEmail
        trainingSubject
        billablePerson
   }
`
export const createAssessmentAdditionalInfoReplication = (
  db: RxDatabase,
  url: GraphQLServerUrl,
  autoStart: boolean
) => {
  const assessmentAdditionalInfoQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      query assessmentAdditionalInfoDocuments(
        $checkpoint: DocumentCheckpointInput
        $first: Int!
      ) {
        assessmentAdditionalInfoDocuments(
          checkpoint: $checkpoint
          first: $first
        ) {
          nodes {
            ...assessmentAdditionalInfo
          }
        }
      }
    `)
  )
  const assessmentAdditionalInfoStreamQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      subscription streamAssessmentAdditionalInfos {
        streamAssessmentAdditionalInfos {
          assessmentAdditionalInfo {
            ...assessmentAdditionalInfo
          }
        }
      }
    `)
  )
  const assessmentAdditionalInfoPushQuery = createQueryWithHash(
    graphql(/* GraphQL*/ `
      mutation addAssessmentAdditionalInfo(
        $rows: [AssessmentAdditionalInfoInput]!
      ) {
        assessmentAdditionalInfoReplicate(input: { items: $rows }) {
          result {
            ...assessmentAdditionalInfo
          }
        }
      }
    `)
  )
  return replicateGraphQL({
    collection: db.assessment_additional_info,
    url,
    credentials: 'include',
    replicationIdentifier: url.http || '',
    autoStart,
    deletedField: 'deleted',
    pull: {
      includeWsHeaders: true,
      streamQueryBuilder: () => {
        return {
          ...assessmentAdditionalInfoStreamQuery,
          variables: {},
        }
      },
      responseModifier: (data: PullData, origin) => {
        // in case of subscriptions, we always only receive a single assignment, so we map it here into the right
        // expected structure
        if (origin === 'stream' && data && 'assessmentAdditionalInfo' in data) {
          return pullResponseModifier({
            nodes: [data.assessmentAdditionalInfo],
          })
        }
        return pullResponseModifier<AssessmentAdditionalInfo>(
          data as {
            nodes: Array<WithDeleted<AssessmentAdditionalInfo>>
          }
        )
      },
      batchSize: BATCH_SIZE,
      queryBuilder: (checkpoint, limit) => {
        return {
          ...assessmentAdditionalInfoQuery,
          variables: {
            checkpoint,
            first: limit,
          },
        }
      },
    },
    push: {
      responseModifier: (data: { result: AssessmentAdditionalInfo[] }) => {
        return data.result
      },
      queryBuilder(
        rows: RxReplicationWriteToMasterRow<AssessmentAdditionalInfo>[]
      ) {
        return {
          ...assessmentAdditionalInfoPushQuery,
          variables: {
            rows: rows.map((row) => row.newDocumentState),
          },
        }
      },
    },
  })
}
